import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface SpeciesStatsResponse {
  monthlyData: Array<Record<string, string | number>>;
  yearlyData: Array<{ year: string; records: number }>;
}

interface SpeciesMonthChartProps {
  orgId: string;
  speciesId: string;
}

const COLORS = [
  'hsl(210, 80%, 50%)',
  'hsl(340, 70%, 55%)',
  'hsl(150, 60%, 45%)',
  'hsl(30, 80%, 55%)',
  'hsl(270, 60%, 55%)',
  'hsl(190, 70%, 45%)',
  'hsl(350, 70%, 60%)',
  'hsl(90, 60%, 45%)',
  'hsl(240, 60%, 60%)',
  'hsl(15, 75%, 55%)',
];

export function SpeciesMonthChart({ orgId, speciesId }: SpeciesMonthChartProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['speciesStats', orgId, speciesId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/species/${speciesId}/stats`);
      if (!res.ok) throw new Error('Failed to fetch species stats');
      return res.json() as Promise<SpeciesStatsResponse>;
    },
    enabled: !!orgId && !!speciesId,
  });

  const years = data?.yearlyData.map((year) => year.year) ?? [];
  const monthlyData = data?.monthlyData ?? [];

  const chartConfig: ChartConfig = {};
  years.forEach((year, i) => {
    chartConfig[year] = {
      label: year,
      color: COLORS[i % COLORS.length],
    };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Records per Month (All Years)</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : years.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No records for this species.</div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <ResponsiveContainer>
              <BarChart data={monthlyData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value, name) => (
                        <>
                          <span className="text-muted-foreground">{name}</span>
                          <span className="text-foreground font-mono font-medium tabular-nums">
                            {value}
                          </span>
                        </>
                      )}
                    />
                  }
                />
                <ChartLegend content={<ChartLegendContent />} />
                {years.map((year) => (
                  <Bar key={year} dataKey={year} stackId="years" fill={chartConfig[year]?.color ?? '#888'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
